import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Moment from 'moment';
import ReactHighcharts from 'react-highcharts';
import { extendMoment } from 'moment-range';
import {
  orderBy, reduce, each, findIndex,
  groupBy, map, first,
} from 'lodash';
import numeral from 'numeral';
import {
  getWinLossOpportunities,
} from '../../../Dashboard/DashboardReducer';
import { getColorFromKey } from '../../../../util/helpers';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import PeriodDropdown from '../../../Analysic/components/rate-graph/period-dropdown';
import PeriodForm from '../../../Analysic/components/rate-graph/period-form';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { format as formatMoney } from '../../../../components/money-display';
import { getIsFontSizeBig } from '../../../App/AppReducer';

const moment = extendMoment(Moment);

const USER_PREFERENCE_PERIOD_TYPE = 'leaderboard-time-chart-period-type';
const USER_PREFERENCE_PERIOD_DAYS = 'outcomes-time-chart-period-days';

class TimeChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      period: 'monthly',
      days: null,
      modalOpen: false,
      config: null,
    };
    this.handlePeriodChange = this.handlePeriodChange.bind(this);
    this.handleCustomPeriodSelect = this.handleCustomPeriodSelect.bind(this);
    this.handlePeriodDayChange = this.handlePeriodDayChange.bind(this);
  }

  componentWillMount() {
    const period = this.context.getUserPreference(USER_PREFERENCE_PERIOD_TYPE, 'monthly');
    const days = this.context.getUserPreference(USER_PREFERENCE_PERIOD_DAYS, 1);
    this.setState({ period, days });
  }

  componentDidMount() {
    this.calculate();
  }

  handlePeriodChange(period) {
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_TYPE, period);
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_DAYS, null);
    this.setState({ period, days: null }, () => {
      this.calculate();
    });
  }

  handleCustomPeriodSelect() {
    this.setState({ modalOpen: true });
  }

  handlePeriodDayChange(days) {
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_TYPE, 'days');
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD_DAYS, days);
    this.setState({ days, period: 'days' }, () => {
      this.calculate();
    });
  }

  calculate() {
    const {
      opportunities, closeDateRange, hasNoDecisionSetup,
      isFontSizeBig, organization,
    } = this.props;
    const {
      period, days,
    } = this.state;
    
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }

    let format = null;
    if (period === 'monthly') {
      format = 'YYYYMM';
    } else if (period === 'quarterly') {
      format = 'YYYYQ';
    } else if (period === 'annual') {
      format = 'YYYY';
    }

    const inputOpportunities = orderBy(opportunities.map((opportunity) => {
      opportunity.close_date = moment(opportunity.close_date, 'YYYY-MM-DD').startOf('day');
      return opportunity;
    }), ['close_date', 'asc']);

    let groupedOpportunities = {};
    if (closeDateRange && closeDateRange.length > 1 && closeDateRange[0] && closeDateRange[1]) {
      let mRange = moment.range(closeDateRange[0], closeDateRange[1]);
      if (period === 'monthly') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('month'), moment(closeDateRange[1]).endOf('month'));
      } else if (period === 'quarterly') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('quarter'), moment(closeDateRange[1]).endOf('quarter'));
      } else if (period === 'annual') {
        mRange = moment.range(moment(closeDateRange[0]).startOf('year'), moment(closeDateRange[1]).endOf('year'));
      }
      let rIt = 'days';
      let step = days;
      if (period === 'monthly') {
        rIt = 'months';
        step = 1;
      } else if (period === 'quarterly') {
        rIt = 'quarters';
        step = 1;
      } else if (period === 'annual') {
        rIt = 'years';
        step = 1;
      }

      const labelRange = Array.from(mRange.by(rIt, { exclusive: false, step }));
      const xLabels = labelRange.map((m, i) => moment.range(m, m.clone().add(step, rIt).subtract(i === labelRange.length - 1 ? 0 : 1, 'days')));
      groupedOpportunities = reduce(xLabels, (items, l) => {
        const k = l.start.format('YYYY-MM-DD');
        items[k] = [];
        return items;
      }, {});

      each(inputOpportunities, (opportunity) => {
        if (opportunity.close_date) {
          const idx = findIndex(xLabels, xl => xl.contains(opportunity.close_date));
          if (idx >= 0) {
            groupedOpportunities[xLabels[idx].start.format('YYYY-MM-DD')].push(opportunity);
          }
        }
      });
    } else if (format) {
      groupedOpportunities = format ? groupBy(inputOpportunities, opportunity => (opportunity.close_date ? opportunity.close_date.format(format) : null)) : {};
    } else if (period === 'days' && days !== null && days !== 0) {
      const minDateOpportunities = inputOpportunities[0];
      const start = minDateOpportunities.close_date;
      groupedOpportunities = groupBy(inputOpportunities, (opportunity) => {
        const d = opportunity.close_date.diff(start, 'days');
        return Math.floor(d / days);
      });
    }

    groupedOpportunities = orderBy(map(groupedOpportunities, (groupedOpportunity, gPeriod) => {
      let count = 0;
      let totalAmount = 0;
      const items = reduce(groupBy(groupedOpportunity, opportunity => opportunity.stage), (result, value, stage) => {
        let amount = 0;
        let feedbackCount = 0;
        each(value, (o) => {
          amount += o.amount || 0;
          if (o.has_requests) {
            feedbackCount += 1;
          }
        });
        result[stage] = {
          amount,
          count: value.length,
          requestPercentage: value.length !== 0 ? (feedbackCount / value.length) * 100 : 0,
          ids: map(value, 'opportunity_id'),
        };
        count += result[stage].count;
        totalAmount += result[stage].amount;
        return result;
      }, {});

      const closeDate = groupedOpportunity.length ? first(groupedOpportunity).close_date : moment(gPeriod, 'YYYY-MM-DD');
      const result = {
        period: gPeriod,
        items,
        count,
        totalAmount,
        closeDate,
      };
      return result;
    }), ['period', 'asc']);


    const series = [
      {
        name: 'Won', color: getColorFromKey('won'), data: [],
      },
      {
        name: 'Lost', color: getColorFromKey('lost'), data: [],
      },
    ];
    if (hasNoDecisionSetup) {
      series.push({
        name: 'No Decision', color: getColorFromKey('noDecision'), data: [],
      });
    }

    const categories = map(groupedOpportunities, (groupedOpportunity) => {
      let f = 'll';
      if (period === 'monthly') {
        f = 'MMM YYYY';
      } else if (period === 'quarterly') {
        f = '[Q]Q YYYY';
      } else if (period === 'annual') {
        f = 'YYYY';
      }
      each(series, (s) => {
        const stage = s.name;
        let y = 0;
        let count = 0;
        let amount = 0;
        if (groupedOpportunity.items[stage]) {
          y = groupedOpportunity.items[stage].requestPercentage || 0;
          count = groupedOpportunity.items[stage].count || 0;
          amount = groupedOpportunity.items[stage].amount || 0;
        }


        const formatedY = `${numeral(y).format('0.0')}%`;
        s.data.push({
          y,
          formatedY,
          count,
          amount: formatMoney(amount, true, 1, symbol, suffix),
          avergaAmount: count !== 0 ? formatMoney(amount / count, true, 1, symbol, suffix) : 0,
          opportunityIds: groupedOpportunity.items[stage] ? groupedOpportunity.items[stage].ids : [],
          events: {
            click: (event) => {
              this.props.onOpportunitySelect(event.point.options.opportunityIds);
            },
          },
        });
      });
      return groupedOpportunity.closeDate.format(f);
    });

    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'line',
        animation: {
          duration: 1000,
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: {
          style: {
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
      },
      tooltip: {
        headerFormat: `<span style="font-weight: bold; ${isFontSizeBig ? 'font-size:16px' : 'font-size:12px'}">{point.key} <span style="color:{series.color};">{series.name}:&nbsp;</span></span>
          <table class="table table-condensed" style="${isFontSizeBig ? 'font-size:16px' : 'font-size:12px'}; margin-top:1em; margin-bottom: 0;">`,
        pointFormat: `<tr>
          <td>Percent with requests:&nbsp;</td>
          <td class="text-right"><b>{point.formatedY}</b></td>
        </tr><tr>
          <td>Deal count:&nbsp;</td>
          <td class="text-right"><b>{point.count}</b></td>
        </tr><tr>
          <td>Total amount:&nbsp;</td>
          <td class="text-right"><b>{point.amount}</b></td>
        </tr><tr>
          <td>Average Size:&nbsp;</td>
          <td class="text-right"><b>{point.avergaAmount}</b></td>
        </tr>`,
        footerFormat: '</table>',
        shared: false,
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
      },
      legend: {
        itemStyle: {
          fontSize: isFontSizeBig ? '16px' : null,
        },
      },
      yAxis: [
        {
          title: {
            text: 'Percentage with Feedback Requests',
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
              fontWeight: 'bold',
            },
          },
          labels: {
            formatter: function f() {
              return `${this.value}%`;
            },
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
            },
          },
          alternateGridColor: 'rgba(66,66,66,0.05)',
          lineWidth: 0,
          plotLines: [{
            value: 0,
            dashStyle: 'dashed',
            color: 'rgba(0, 0, 0, 0.5)',
            width: 2,
            label: {
              text: null,
            },
          }],
        },
      ],
      plotOptions: {
        series: {
          marker: {
            fillColor: '#ffffff',
            lineWidth: 2,
            radius: 5,
            lineColor: null, // inherit from series
          },
        },
      },
      series,
    };

    this.setState({ config });
  }

  render() {
    const {
      period, days, modalOpen, config,
    } = this.state;
    const { opportunities } = this.props;
    return (
      <div className="animated fadeIn">
        <div>
          <h5 className="sub-header-with-controls">
            View percent of deals with feedback requests by&nbsp;
            &nbsp;
            <PeriodDropdown
              period={period}
              days={days}
              onChange={this.handlePeriodChange}
              onCustomPeriodSelect={this.handleCustomPeriodSelect}
            />
            &nbsp;
            periods
          </h5>
        </div>
        <div>
          {config !== null && <ReactHighcharts config={config} isPureConfig />}
        </div>
        <PeriodForm
          maxValue={opportunities.length}
          modalOpen={modalOpen}
          onDayChange={this.handlePeriodDayChange}
          onHide={() => { this.setState({ modalOpen: false }); }}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    opportunities: getWinLossOpportunities(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
    isFontSizeBig: getIsFontSizeBig(state),
    organization: getUserCurrentOrganization(state),
  };
}

TimeChart.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  closeDateRange: PropTypes.arrayOf(PropTypes.instanceOf(moment)),
  hasNoDecisionSetup: PropTypes.bool,
  isFontSizeBig: PropTypes.bool,
  onOpportunitySelect: PropTypes.func,
  organization: PropTypes.shape({
    organization_id: PropTypes.string.isRequired,
  }).isRequired,
};

TimeChart.defaultProps = {
  closeDateRange: [],
  hasNoDecisionSetup: false,
  isFontSizeBig: false,
  onOpportunitySelect: () => {},
};

TimeChart.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  getUserPreference: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(TimeChart);
